import { useCallback, useMemo } from 'react';

import { routes } from '~constants/routes';
import type { PaymentType } from '~context/paywall/types';
import { sendEvent } from '~features/annals';
import { useUserId } from '~hooks/useUserId';
import { usePathname } from '~i18n/navigation';
import type { TMotoEvent } from '~logger/types';
import { convertToBackendId } from '~utils/ids';

import type { PaymentReason } from '../enums/payment';
import { PaymentReasonType } from '../enums/payment';
import eventsCheckoutNames from '../events/checkout';
import { useSendEvent } from '../useSendEvent';

const usePaymentReasonType = () => {
	const pathname = usePathname();

	const reasonType = useMemo(() => {
		if (
			pathname === routes.explore.home ||
			pathname.startsWith(routes.explore.models)
		) {
			return PaymentReasonType.Grid;
		}

		if (pathname === routes.chats.home) {
			return PaymentReasonType.Chats;
		}

		if (pathname.startsWith(routes.chats.home)) {
			return PaymentReasonType.Chat;
		}

		if (pathname === routes.generate.character) {
			return PaymentReasonType.GToolBot;
		}

		if (
			pathname.startsWith(routes.generate.images.home) ||
			pathname.startsWith(routes.generate.videos.home)
		) {
			return PaymentReasonType.GToolMedia;
		}

		if (pathname.startsWith(routes.profile.home)) {
			return PaymentReasonType.BotProfile;
		}

		return PaymentReasonType.Unknown;
	}, [pathname]);

	return reasonType;
};

const useOpenStore = () =>
	useSendEvent<{
		reason?: PaymentReason;
		reasonType?: PaymentReasonType;
		bot_UserId?: number | string;
		bot_name?: string;
		type: PaymentType;
	}>(eventsCheckoutNames.openStore);

const usePaymentOpenClicked = ({
	reason,
	botClientId,
	type,
	reasonType,
}: {
	reason?: PaymentReason;
	reasonType?: PaymentReasonType;
	botClientId?: number | string;
	type: PaymentType;
}) => {
	const params = useMemo(
		() => ({
			reason,
			bot_UserId: botClientId,
			type,
			reasonType,
		}),
		[type, botClientId, reason, reasonType]
	);

	return useSendEvent(eventsCheckoutNames.paymentOpenClick, params);
};

const useAnnalsOnProductSelected = ({
	reason,
	botClientId,
	type,
	reasonType,
}: {
	reason?: PaymentReason;
	reasonType?: PaymentReasonType;
	botClientId?: number | string;
	type: PaymentType;
}) => {
	const params = useMemo(
		() => ({
			reason,
			bot_UserId: botClientId,
			type,
			reasonType,
		}),
		[type, botClientId, reason, reasonType]
	);

	return useSendEvent(eventsCheckoutNames.paymentOnSelectPack, params);
};

const useBeginCheckout = ({
	sku,
	reason,
	reasonType,
	botClientId,
	botName,
}: {
	sku: string;
	reason?: PaymentReason;
	reasonType?: PaymentReasonType;
	botClientId?: number | string;
	botName?: string;
}) => {
	const params = useMemo(
		() => ({
			sku,
			reason,
			reasonType,
			bot_UserId: botClientId,
			bot_name: botName,
		}),
		[botClientId, botName, reason, reasonType, sku]
	);

	return useSendEvent(eventsCheckoutNames.beginCheckout, params);
};

const usePromocodeApplied = ({
	reason,
	reasonType,
	type,
}: {
	reason?: PaymentReason;
	reasonType?: PaymentReasonType;
	type: PaymentType;
}) => {
	const params = useMemo(
		() => ({
			reason,
			reasonType,
			type,
		}),
		[reason, reasonType, type]
	);

	return useSendEvent(eventsCheckoutNames.promocodeApplied, params);
};

const useBeginCheckoutPopupShow = ({
	reason,
	reasonType,
	type,
}: {
	reason?: PaymentReason;
	reasonType?: PaymentReasonType;
	type: PaymentType;
}) => {
	const params = useMemo(
		() => ({
			reason,
			reasonType,
			type,
		}),
		[reason, reasonType, type]
	);

	return useSendEvent(eventsCheckoutNames.beginCheckoutPopupShow, params);
};

const useBeginCheckoutPopupClickBack = ({
	reason,
	reasonType,
	type,
}: {
	reason?: PaymentReason;
	reasonType?: PaymentReasonType;
	type: PaymentType;
}) => {
	const params = useMemo(
		() => ({
			reason,
			reasonType,
			type,
		}),
		[reason, reasonType, type]
	);

	return useSendEvent(eventsCheckoutNames.beginCheckoutPopupClickBack, params);
};

const useBeginCheckoutPopupClickDecline = ({
	reason,
	reasonType,
	type,
}: {
	reason?: PaymentReason;
	reasonType?: PaymentReasonType;
	type: PaymentType;
}) => {
	const params = useMemo(
		() => ({
			reason,
			reasonType,
			type,
		}),
		[reason, reasonType, type]
	);

	return useSendEvent(
		eventsCheckoutNames.beginCheckoutPopupClickDecline,
		params
	);
};

const useMotoEventProxy = ({
	sku,
	reason,
	reasonType,
	botClientId,
	botName,
}: {
	sku: string;
	reason?: PaymentReason;
	reasonType?: PaymentReasonType;
	botClientId?: number | string;
	botName?: string;
}) => {
	const userId = useUserId();
	const params = useMemo(
		() => ({
			sku,
			reason,
			reasonType,
			bot_UserId: botClientId,
			bot_name: botName,
		}),
		[botClientId, botName, reason, reasonType, sku]
	);

	return useCallback(
		async ({
			payload,
			type,
		}: {
			payload?: Record<string, unknown>;
			type: TMotoEvent;
		}) => {
			return sendEvent({
				userId,
				event: type,
				body: {
					...params,
					...payload,
					bot_UserId: params.bot_UserId
						? convertToBackendId(params.bot_UserId as string)
						: undefined,
					timestamp: (Date.now() / 1000) | 0,
				},
			});
		},
		[params, userId]
	);
};

// TODO: should be removed, see note for OpenStoreDigitalCopy
const useOpenDigitalCopySubscription = () =>
	useSendEvent<{
		bot_UserId?: number | string;
		bot_name?: string;
	}>(eventsCheckoutNames.openDigitalCopySubscription);

// TODO: should be removed, see note for BeginCheckoutDigitalCopy
const useBeginCheckoutDigitalCopy = ({
	sku,
	reason,
	reasonType,
	botClientId,
	botName,
}: {
	sku: string;
	reason?: PaymentReason;
	reasonType?: PaymentReasonType;
	botClientId?: number | string;
	botName?: string;
}) => {
	const params = useMemo(
		() => ({
			sku,
			reason,
			reasonType,
			bot_UserId: botClientId,
			bot_name: botName,
		}),
		[botClientId, botName, reason, reasonType, sku]
	);

	return useSendEvent(eventsCheckoutNames.beginCheckoutDigitalCopy, params);
};

const annalsCheckout = {
	usePaymentReasonType,
	useOpenStore,
	usePaymentOpenClicked,
	useAnnalsOnProductSelected,
	useBeginCheckout,
	usePromocodeApplied,
	useMotoEventProxy,
	useOpenDigitalCopySubscription,
	useBeginCheckoutDigitalCopy,
	useBeginCheckoutPopupClickBack,
	useBeginCheckoutPopupClickDecline,
	useBeginCheckoutPopupShow,
};

export default annalsCheckout;
